/* ============================================================
   Shared subscription pricing model — used by both the public
   SubscriptionConfigurator and the PublicOnboarding signup flow
   so the numbers always match.

   Schools (per child) bill PER TERM.   Premium Ksh 650; Enterprise 750 on a
     group's first 3 campuses, 850 on the 4th and after (2026-10-29).
   Fleets (per vehicle) bill PER MONTH in bands, whatever the plan: vehicles
     1–10 Ksh 2,000, 11–30 1,500, every one after 30 1,200 (2026-10-29).
   Both are bands, like tax bands: growing never reprices what a customer
   already has. 31 vehicles = 10 × 2,000 + 20 × 1,500 + 1 × 1,200.
   Families (per household) bills PER MONTH. Ksh 1,200 flat, plus one add-on.
   Keep SCHOOL_PRICE in sync with pear-payments-backend/src/pricing.js.

   Basic was removed as a sellable plan (2026-08-21). The 'basic' id remains
   only so legacy org docs / quotes can still be read until migration flips
   them to premium. New checkouts and plan pickers offer Premium + Enterprise.
   ============================================================ */

export type AccountUnit = 'child' | 'vehicle' | 'household';
/** Includes legacy 'basic' for reading old org docs — never offer it for sale
 *  to a SCHOOL or LORRY. It is still the live, and only, Families plan. */
export type PlanId = 'pilot' | 'basic' | 'premium' | 'enterprise';
/** Plans a customer can pick or be charged for today. */
export type PaidPlanId = 'premium' | 'enterprise';
// Billing periods:
//   'termly' — schools only: one term's amount, charged at each term START from the
//              calendar the school paints in onboarding (the real "per term" promise).
//   'term'   — a flat quarter (91 days) from the payment date; kept for schools that
//              prefer a fixed cycle. Labelled "Quarterly" in the UI.
//   'month'  — monthly (lorry's native cycle).
//   'year'   — annual, 10% off.
// A school year is THREE terms, so quarterly and monthly split that year's full
// price into 4 and 12 equal payments. Until 2026-09-14 they charged a whole term
// per quarter and a third of a term per month — four terms a year, a third more
// than paying per term, and the reason the pricing page showed Quarterly and
// Per term as the same figure.
export type BillingPeriod = 'term' | 'termly' | 'month' | 'year';

export const ANNUAL_DISCOUNT = 0.1; // 10% off annually
export const TERMS_PER_YEAR = 3;
export const QUARTERS_PER_YEAR = 4;
export const MONTHS_PER_YEAR = 12;

/* ---------------------------------------------------------------------------
   Pilot window. The account runs FREE for PILOT_DAYS; the first real charge
   lands PILOT_BILL_DELAY_DAYS *after* that (so the last free day is never also
   the day the card is hit). Mirror of PILOT_DAYS / PILOT_BILL_DELAY_DAYS in
   pear-payments-backend/src/pricing.js — keep the two in sync.
   --------------------------------------------------------------------------- */
export const PILOT_DAYS = 14;           // 2-week free pilot
export const PILOT_BILL_DELAY_DAYS = 1; // billed the day after the pilot ends
export const PILOT_LENGTH_LABEL = '2-week';

function addDays(from: Date, days: number): Date {
  const d = new Date(from.getTime());
  d.setDate(d.getDate() + days);
  return d;
}

/** Last free day of the pilot. */
export function pilotEndDate(from: Date = new Date()): Date {
  return addDays(from, PILOT_DAYS);
}

/** The day the first real charge is taken — one day after the pilot ends. */
export function pilotBillDate(from: Date = new Date()): Date {
  return addDays(pilotEndDate(from), PILOT_BILL_DELAY_DAYS);
}

export function fmtDate(d: Date): string {
  return d.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Each enabled add-on (fuel sensor, GPS, fees module) costs +50
// per unit per short cycle — i.e. +50/child/term for schools, +50/vehicle/month for lorry.
export const ADDON_PRICE = 50;
export function addonRate(addonCount = 0): number {
  return ADDON_PRICE * Math.max(0, addonCount);
}

// schools: per child PER TERM. Legacy basic kept for quote math until migration.
// Enterprise is the first-3-campus rate; enterpriseSchoolRate() prices a campus.
export const SCHOOL_PRICE: Record<PlanId, number> = { pilot: 0, basic: 400, premium: 650, enterprise: 750 };
export const ENTERPRISE_LATER_CAMPUS = { fromCampus: 4, price: 850 };
// lorry: per vehicle PER MONTH. Premium and Enterprise hold the first band;
// fleetMonth() prices a fleet. Mirror of FLEET_BANDS in pricing.js.
export const LORRY_PRICE: Record<PlanId, number> = { pilot: 0, basic: 250, premium: 2000, enterprise: 2000 };
export const FLEET_BANDS: { upTo: number; price: number }[] = [
  { upTo: 10, price: 2000 },
  { upTo: 30, price: 1500 },
  { upTo: Infinity, price: 1200 },
];

/** How a fleet's vehicles fall into the bands: [{ count: 10, price: 2000 }, …]. */
export function fleetBands(vehicles: number): { count: number; price: number }[] {
  const n = Math.max(0, Math.floor(Number(vehicles) || 0));
  const out: { count: number; price: number }[] = [];
  let from = 0;
  for (const band of FLEET_BANDS) {
    if (n <= from) break;
    out.push({ count: Math.min(n, band.upTo) - from, price: band.price });
    from = band.upTo;
  }
  return out;
}

/** A fleet's month before add-ons: every vehicle at the price of its band. */
export function fleetMonth(vehicles: number): number {
  return fleetBands(vehicles).reduce((sum, b) => sum + b.count * b.price, 0);
}

/** "10 × 2,000 + 2 × 1,500" — the sum behind a fleet's month. */
export function fleetBandsWords(vehicles: number): string {
  return fleetBands(vehicles).map((b) => `${b.count.toLocaleString('en-KE')} × ${b.price.toLocaleString('en-KE')}`).join(' + ');
}

/** An Enterprise campus's per-child term, by its place in the group (1 = the mother). */
export function enterpriseSchoolRate(campus = 1): number {
  return (Number(campus) || 1) >= ENTERPRISE_LATER_CAMPUS.fromCampus ? ENTERPRISE_LATER_CAMPUS.price : SCHOOL_PRICE.enterprise;
}

/** An org's place in its Enterprise group: 1 for the mother or a school outside
 *  one, then each branch in the order it joined the mother's list. Same rule as
 *  campusOf in pear-payments-backend/src/groupPricing.js; without the mother's
 *  list a branch reads as campus 1, the lower rate. */
export function campusOf(code: string, org: Readonly<Record<string, unknown>>, mother?: Readonly<Record<string, unknown>> | null): number {
  if (!org.parentOrgCode || !mother || !Array.isArray(mother.branchOrgCodes)) return 1;
  const at = (mother.branchOrgCodes as unknown[]).indexOf(code);
  return at < 0 ? 1 : at + 2;
}

/** What a price depends on beyond plan and unit: a fleet's size, a campus's place in its group. */
export interface RateBasis { size?: number; campus?: number }
// Trasse Families: one household, one flat price PER MONTH. Base is tracking
// for two children. The add-on is analytics plus unlimited children.
// Mirror of HOUSEHOLD_PRICE in pear-payments-backend/src/pricing.js.
export const HOUSEHOLD_PRICE: Record<PlanId, number> = { pilot: 0, basic: 1200, premium: 1200, enterprise: 1200 };
/** Household add-on (analytics + unlimited children), on top of the KES 1,200 base. */
export const HOUSEHOLD_ANALYTICS_KES = 300;

export interface PlanDef {
  id: PlanId;
  name: string;
  desc: string;
  rec?: boolean;
  feats: string[];
}

/** Sellable + pilot cards shown on pricing / signup. Basic is intentionally absent. */
export const PLAN_DEFS: PlanDef[] = [
  { id: 'pilot', name: 'Pilot', desc: 'Try trasse with a real route or fleet — free.', feats: ['Up to 1 bus / 5 vehicles', 'Live tracking & alerts', 'Full features for 2 weeks'] },
  { id: 'premium', name: 'Premium', desc: 'Tracking plus Zana Mode — your AI fleet copilot.', rec: true, feats: ['Live map, ETAs & alerts', 'Zana Mode — AI fleet insights', 'Fuel, route & driver analytics', 'Attendance & announcements', 'Priority support'] },
  { id: 'enterprise', name: 'Enterprise', desc: 'Multi-branch fleets & school groups, with Zana.', feats: ['Everything in Premium', 'Higher Zana AI allowances', 'Multiple branches / depots', 'Dedicated account manager', 'API & data export'] },
];

export const SELLABLE_PLAN_DEFS = PLAN_DEFS.filter((p) => p.id === 'premium' || p.id === 'enterprise');

/** True for a fleet plan priced by FLEET_BANDS: free stays free, legacy basic keeps its row. */
export function isBandedFleet(planId: PlanId, unit: AccountUnit): boolean {
  return unit === 'vehicle' && (planId === 'premium' || planId === 'enterprise');
}

/** Per-unit price. For a banded fleet it is the AVERAGE per vehicle at that size
 *  (rounded): show it as "on average", and fleetBandsWords() for the sum. */
export function rate(planId: PlanId, unit: AccountUnit, basis: RateBasis = {}): number {
  if (unit === 'household') return HOUSEHOLD_PRICE[planId];
  if (unit === 'child') return planId === 'enterprise' ? enterpriseSchoolRate(basis.campus) : SCHOOL_PRICE[planId];
  if (isBandedFleet(planId, unit)) {
    const n = Math.max(1, Math.floor(basis.size ?? 1));
    return Math.round(fleetMonth(n) / n);
  }
  return LORRY_PRICE[planId];
}

/** Default short cycle for the unit: real term-calendar billing for schools,
 *  monthly for lorry. */
export function shortPeriod(unit: AccountUnit): BillingPeriod {
  return unit === 'child' ? 'termly' : 'month';
}

/** A household is always one subscription — never a countable fleet. */
export function isHousehold(unit: AccountUnit): boolean {
  return unit === 'household';
}

export function unitWord(unit: AccountUnit, n = 2): string {
  if (unit === 'household') return n === 1 ? 'household' : 'households';
  if (unit === 'child') return n === 1 ? 'child' : 'children';
  return n === 1 ? 'vehicle' : 'vehicles';
}

/** Short label used in "Ksh X / child · term" style rate lines. */
export function perUnitSuffix(unit: AccountUnit): string {
  return unit === 'child' ? 'term' : 'mo';
}

/** Suffix after a total amount: term / quarter / mo / yr. */
export function totalSuffix(unit: AccountUnit, period: BillingPeriod): string {
  if (unit === 'household') return 'mo';
  if (period === 'year') return 'yr';
  if (period === 'month') return 'mo';
  if (unit !== 'child') return 'mo';
  return period === 'term' ? 'quarter' : 'term';
}

/** "billed X" wording. */
export function billedWord(unit: AccountUnit, period: BillingPeriod): string {
  if (unit === 'household') return 'monthly';
  if (period === 'year') return 'annually';
  if (period === 'month') return 'monthly';
  if (period === 'term') return unit === 'child' ? 'quarterly' : 'monthly';
  return 'per term (on your term dates)';
}

/** Effective per-unit rate incl. add-ons. Free plans (pilot) stay free, add-ons and all. */
export function perUnitRate(planId: PlanId, unit: AccountUnit, addonCount = 0, basis: RateBasis = {}): number {
  if (unit === 'household') {
    const base = rate(planId, unit);
    if (base === 0) return 0;
    return base + HOUSEHOLD_ANALYTICS_KES * (addonCount > 0 ? 1 : 0);
  }
  const planRate = rate(planId, unit, basis);
  return planRate === 0 ? 0 : planRate + addonRate(addonCount);
}

/** One short cycle before cadence: a term for schools, a month for fleets.
 *  Same arithmetic as chargeAmountKES's shortTotal in pricing.js. */
function cycleBase(planId: PlanId, size: number, unit: AccountUnit, addonCount: number, campus: number): number {
  if (isBandedFleet(planId, unit)) return fleetMonth(size) + addonRate(addonCount) * Math.max(0, size);
  return perUnitRate(planId, unit, addonCount, { campus }) * size;
}

/** Total bill for a plan at a given size, unit, period and add-on count.
 *  `campus` is an Enterprise school's place in its group (campusOf). */
export function bill(planId: PlanId, size: number, unit: AccountUnit, period: BillingPeriod, addonCount = 0, campus = 1): number {
  if (unit === 'household') return perUnitRate(planId, unit, addonCount);
  const base = cycleBase(planId, size, unit, addonCount, campus); // per term (school) or per month (lorry)
  if (period === 'year') {
    const cycles = unit === 'child' ? TERMS_PER_YEAR : MONTHS_PER_YEAR;
    return Math.round(base * cycles * (1 - ANNUAL_DISCOUNT));
  }
  // Schools on a quarterly or monthly cycle pay their three-term year split evenly.
  // Same arithmetic in the same order as chargeAmountKES in pricing.js: the two
  // must round to the same shilling, or checkout charges what the screen never showed.
  if (unit === 'child' && period === 'term') return Math.round((base * TERMS_PER_YEAR) / QUARTERS_PER_YEAR);
  if (unit === 'child' && period === 'month') return Math.round((base * TERMS_PER_YEAR) / MONTHS_PER_YEAR);
  return base; // 'termly' (term dates), lorry 'month'
}

/** A school's full year — three terms — before the annual discount. Quarterly
 *  and monthly bills are this ÷ 4 and ÷ 12, and the pricing screens show it so
 *  a school can see that every way of paying adds up to the same year. */
export function schoolYearBill(planId: PlanId, size: number, addonCount = 0, campus = 1): number {
  return perUnitRate(planId, 'child', addonCount, { campus }) * size * TERMS_PER_YEAR;
}

/** True for the cycles that split a school's year into equal parts. */
export function isYearSplit(unit: AccountUnit, period: BillingPeriod): boolean {
  return unit === 'child' && (period === 'term' || period === 'month');
}

/** The divisor, in words: "Ksh 546,000 a year ÷ 4 quarters". */
export function yearSplitWord(period: BillingPeriod): string {
  return period === 'month' ? '12 months' : '4 quarters';
}

/** Annual savings amount versus paying each short cycle at full price. */
export function annualSaving(planId: PlanId, size: number, unit: AccountUnit, addonCount = 0, campus = 1): number {
  const base = cycleBase(planId, size, unit, addonCount, campus);
  const cycles = unit === 'child' ? TERMS_PER_YEAR : MONTHS_PER_YEAR;
  return Math.round(base * cycles * ANNUAL_DISCOUNT);
}

/** Per-short-cycle figure when billed yearly (year total ÷ cycles). */
export function perCycleWhenYearly(planId: PlanId, size: number, unit: AccountUnit, addonCount = 0, campus = 1): number {
  const cycles = unit === 'child' ? TERMS_PER_YEAR : MONTHS_PER_YEAR;
  return Math.round(bill(planId, size, unit, 'year', addonCount, campus) / cycles);
}

/** Plan ordering — used to raise (never lower) a picked plan. Legacy basic ranks below. */
export const PLAN_RANK: Record<'basic' | PaidPlanId, number> = { basic: 1, premium: 2, enterprise: 3 };

/**
 * The plan an account actually lands on: the one they picked, raised by any
 * upgrade they switched on during Setup. Zana Mode is included in Premium;
 * running several campuses needs Enterprise. Raising only — an upgrade toggle
 * can never quietly downgrade the plan they chose.
 */
export function effectivePlan(
  picked: PaidPlanId,
  opts: { zanaMode?: boolean; multiBranch?: boolean } = {},
): PaidPlanId {
  let out = picked;
  if (opts.zanaMode && PLAN_RANK.premium > PLAN_RANK[out]) out = 'premium';
  if (opts.multiBranch && PLAN_RANK.enterprise > PLAN_RANK[out]) out = 'enterprise';
  return out;
}

/** Map a URL / stored plan id onto a sellable paid plan (legacy basic → premium). */
export function normalizePaidPlan(value: string | null | undefined): PaidPlanId {
  if (value === 'enterprise') return 'enterprise';
  return 'premium';
}

export function recommendPlan(unit: AccountUnit, size: number): PaidPlanId {
  if (unit === 'child') {
    if (size <= 800) return 'premium';
    return 'enterprise';
  }
  if (size <= 30) return 'premium';
  return 'enterprise';
}

export function fmt(n: number): string {
  return n.toLocaleString('en-KE');
}

/** Checkout / billing currency. Sticky on the org after first successful quote. */
export type BillingCurrency = 'KES' | 'USD';

/** Same default as pear-payments-backend `USD_PER_KES` — override with REACT_APP_USD_PER_KES. */
export const USD_PER_KES = (() => {
  const n = parseFloat(process.env.REACT_APP_USD_PER_KES || '0.0077');
  return Number.isFinite(n) && n > 0 ? n : 0.0077;
})();

/** Convert a KES catalog amount to the display/Paystack major units for `currency`. */
export function amountInCurrency(amountKES: number, currency: BillingCurrency): number {
  if (!Number.isFinite(amountKES) || amountKES <= 0) return 0;
  if (currency === 'USD') {
    // Match backend toPaystackAmount: round to cents, then back to dollars.
    return Math.round(amountKES * USD_PER_KES * 100) / 100;
  }
  return Math.round(amountKES);
}

/** "Ksh 12,345" or "$95.06" — always from the KES catalog price. */
export function money(amountKES: number, currency: BillingCurrency = 'KES'): string {
  if (currency === 'USD') {
    return `$${amountInCurrency(amountKES, 'USD').toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  return `Ksh ${fmt(amountInCurrency(amountKES, 'KES'))}`;
}
